/**
 * All onboarding text: tooltips, info tips, hints and tour steps.
 * Product reviews this file before each phase ships, so keep wording here
 * rather than inline in components.
 */

export const offerFormTips = {
  currency: 'Offers on Musicade are priced and paid in US dollars.',
  price: 'The total you’re offering for the whole job, before fees.',
  paymentType:
    'Single payment: the full price is paid once. Milestone based: split the price into stages, each paid when it’s due.',
  milestones:
    'Each milestone is a stage of the work with its own amount and due date. They must add up to 100% of the price.',
  revisions:
    'How many rounds of changes are included in the price. Extra rounds can be agreed separately.',
  expiration:
    'After this date and time the offer can no longer be accepted. You can send a new one if it lapses.',
  timezone: 'The expiration time is read in this time zone, so both sides see the same deadline.',
  internalNotes: 'Private to you. The recipient never sees these notes.',
  recipient: 'The person who will receive this offer in their Inbox.',
} as const;

/** Refund terms per cancellation policy, shown beside the policy picker. */
export const cancellationPolicyTerms = {
  flexible: {
    label: 'Flexible',
    terms: '100% refund if cancelled at least 48 hours before the gig.',
  },
  moderate: {
    label: 'Moderate',
    terms:
      '100% refund at least 7 days before; 50% refund between 7 days and 48 hours; no refund within 48 hours.',
  },
  strict: {
    label: 'Strict',
    terms:
      '100% refund within 48 hours of booking if the gig is at least 14 days away; 50% refund at least 7 days before the gig; no refund within 7 days.',
  },
} as const;

export const feeTips = {
  processingFee:
    'Card processing charged by Stripe on each payment. It’s added to the total charged at checkout.',
  commission:
    'Musicade’s service fee. It’s deducted from the payout, so the payer is not charged for it.',
  payout: 'What is received after Musicade’s commission.',
  creatorTotal: 'The base amount plus the processing fee: the total charged at checkout.',
} as const;

export const paymentTips = {
  requestPayment:
    'Creates a secure Stripe payment link for the amount you choose and sends it to the gig creator to pay.',
  generateLink:
    'Creates a secure Stripe payment link for the amount you choose. Send it to the person paying.',
  pay: 'Opens the secure Stripe checkout for the requested amount. The processing fee is included.',
  waitingForRequest:
    'On gig offers, the person doing the gig requests each payment. You’ll get a Pay button when they do.',
  recordOffPlatform:
    'Paid in cash, by bank transfer or another app? Record it here so the offer balance stays accurate. The other side must confirm it.',
  confirmSettlement:
    'Only confirm if the money actually changed hands. Once confirmed, it counts toward the offer balance.',
  rejectSettlement:
    'Reject it if you didn’t send or receive this payment. It won’t count toward the balance.',
  stripeSetup:
    'You need a connected Stripe account to receive card payments. It takes a few minutes.',
  minimum: 'Each payment must be at least $10.',
} as const;

export const paymentTagTips = {
  stripe: 'Paid by card through Musicade’s secure Stripe checkout.',
  offPlatform: 'Paid outside Musicade (cash, bank transfer or another app) and recorded here.',
} as const;

/** Keyed by the label shown in the UI, lower-cased. */
export const paymentStatusTips: Record<string, string> = {
  paid: 'The payment went through and counts toward the offer.',
  confirmed: 'Both sides agreed this outside payment happened. It counts toward the offer.',
  rejected: 'The other side said this payment didn’t happen. It doesn’t count toward the offer.',
  'not paid': 'This payment link expired or failed before it was paid.',
  'awaiting confirmation':
    'Recorded as paid outside Musicade. Waiting for the other side to confirm it.',
  'awaiting payment': 'A payment link was created and hasn’t been paid yet.',
  pending: 'Started but not finished yet.',
  processing: 'The payment is being processed. This usually takes a moment.',
  completed: 'The payment went through.',
  succeeded: 'The payment went through.',
  failed: 'The payment didn’t go through. No money was taken.',
  refunded: 'The money was returned to the payer.',
};

/** Keyed by offer status value. */
export const offerStatusTips: Record<string, string> = {
  draft: 'Only you can see this offer. Finish and send it when you’re ready.',
  awaiting_you: 'Review the terms, then accept, ask for changes or decline.',
  withdrawn: 'The sender withdrew this offer, so it can’t be accepted.',
  sent: 'Waiting for the recipient to accept, ask for changes or decline.',
  pending: 'Waiting for the recipient to accept, ask for changes or decline.',
  viewed: 'The recipient has opened the offer but hasn’t replied yet.',
  changes_requested: 'The recipient asked for changes. Edit the offer and send it again.',
  accepted: 'Both sides agreed. Payments can now be requested and made.',
  awaiting_funding: 'Accepted. Waiting for the first payment to be made.',
  payment_due: 'Accepted. A payment is due from you.',
  in_progress: 'Work is underway. Some of the price has been paid.',
  completed: 'The job is done and fully paid.',
  paid: 'The full price has been paid.',
  declined: 'The recipient turned this offer down.',
  rejected: 'The recipient turned this offer down.',
  expired: 'The expiration passed before it was accepted. You can send a new offer.',
  cancelled: 'This offer was cancelled and can’t be accepted.',
  canceled: 'This offer was cancelled and can’t be accepted.',
};

/** Keyed by applicant status value. */
export const applicantStatusTips: Record<string, string> = {
  pending: 'New applicant. Review their profile, then accept or reject.',
  accepted: 'You accepted this applicant. Send them an offer to agree terms and pay.',
  rejected: 'You turned this applicant down. They’ve been notified.',
  shortlisted: 'Saved for later so you can compare applicants.',
  withdrawn: 'The applicant withdrew their application.',
};

interface StepCopy {
  title: string;
  body: string;
}

/** Tour titles and step text. */
export const tourCopy = {
  hiring: {
    title: 'Hiring on Musicade',
    description: 'Post a gig, review applicants, send offers and track payments.',
    tabs: {
      title: 'Your hiring hub',
      body: 'Everything about the gigs you post lives here, in four tabs.',
    },
    inquiries: {
      title: 'Inquiries',
      body: 'Messages from people asking about your gigs before they apply.',
    },
    applicants: {
      title: 'Applicants',
      body: 'Everyone who applied to your gigs. Open an applicant to view their profile, then accept or reject them.',
    },
    applicantActions: {
      title: 'Accept, then send an offer',
      body: 'Accepting an applicant lets you create an offer for them with the price, schedule and terms.',
    },
    offers: {
      title: 'Offers',
      body: 'Offers you’ve sent and received, with their status. Open one to see payments.',
    },
    createOffer: {
      title: 'Create an offer',
      body: 'You can also send an offer to anyone directly, without a gig post.',
    },
    payments: {
      title: 'Payment History',
      body: 'Every payment on your offers, paid by card through Stripe or recorded as paid outside Musicade.',
    },
  },
  offerForm: {
    title: 'Writing an offer',
    description: 'What each part of the offer form means.',
    recipient: {
      title: 'Who it’s for',
      body: 'The offer goes to this person’s Inbox. They can accept it, ask for changes or decline.',
    },
    price: {
      title: 'Price',
      body: 'The total for the whole job, before fees.',
    },
    schedule: {
      title: 'How it’s paid',
      body: 'Pay it all at once, or split it into milestones that are each paid when they’re due.',
    },
    expiration: {
      title: 'Expiration',
      body: 'The offer can’t be accepted after this time, so the other side knows when to reply.',
    },
    fees: {
      title: 'Fees',
      body: 'See the fees on this offer and the total that will be charged, before you send.',
    },
    chat: {
      title: 'Where it shows up',
      body: 'Once sent, the offer appears as a card in your conversation in Messages, and in My Gigs under Offers.',
    },
  },
  offer: {
    summary: {
      title: 'The agreed terms',
      body: 'Price, schedule and the other terms both sides agreed to.',
    },
    respond: {
      title: 'Your reply',
      body: 'Accept the offer, ask for changes to the terms, or decline it.',
    },
    schedule: {
      title: 'Payment schedule',
      body: 'Each payment on this offer and whether it’s been paid.',
    },
    settlement: {
      title: 'Confirm outside payments',
      body: 'When the other side records a payment made outside Musicade, confirm it if the money arrived, or reject it if it didn’t.',
    },
    offPlatform: {
      title: 'Paid outside Musicade?',
      body: 'Paid in cash, by bank transfer or another app? Record it so the balance stays accurate. The other side confirms it before it counts.',
    },
    paymentsList: {
      title: 'Payments',
      body: 'Every payment on this offer, tagged Stripe or Off-platform, with its status.',
    },
  },
  offerGigCreator: {
    title: 'Paying for a gig',
    description: 'How you pay the person you hired.',
    action: {
      title: 'Wait for a payment request',
      body: 'On gig offers, the person doing the gig requests each payment. When they do, a Pay button appears here and you pay securely by card.',
    },
  },
  offerGigRecipient: {
    title: 'Getting paid for a gig',
    description: 'How you request payment from the gig creator.',
    action: {
      title: 'Request payment',
      body: 'Connect Stripe once, then request each payment when it’s due. The gig creator gets a Pay button and pays by card.',
    },
  },
  offerStandaloneCreator: {
    title: 'Getting paid for an offer',
    description: 'How you collect payment on an offer you sent.',
    action: {
      title: 'Generate a payment link',
      body: 'Create a secure Stripe link for the amount due and send it to the recipient to pay.',
    },
  },
  offerStandaloneRecipient: {
    title: 'Paying an offer',
    description: 'How you pay an offer you received.',
    action: {
      title: 'Pay when a link arrives',
      body: 'The sender shares a payment link when a payment is due. You’ll see a Pay button here.',
    },
  },
  takeTour: 'Take the tour',
} satisfies Record<string, string | Record<string, string | StepCopy>>;

export const checklistCopy = {
  title: 'Getting started',
  collapsedLabel: 'Getting started',
  hide: 'Hide checklist',
  items: {
    stripe: {
      label: 'Connect Stripe to get paid',
      hint: 'Needed to receive card payments for gigs and offers.',
    },
    gig: {
      label: 'Post a gig',
      hint: 'Add the role, dates and budget. Applicants will appear here in My Gigs.',
    },
  },
} as const;

export const hints = {
  offerPayments: {
    id: 'offer.payments-intro.v1',
    title: 'How payments work on this offer',
    gigRecipient:
      'You request each payment when it’s due. The gig creator gets a Pay button and pays by card. Paid another way? Record it and they’ll confirm.',
    gigCreator:
      'The person doing the gig requests each payment. You’ll see a Pay button when they do. Paid another way? Record it and they’ll confirm.',
    standaloneCreator:
      'Generate a payment link for the amount due and send it to the recipient. Paid another way? Record it and they’ll confirm.',
    standaloneRecipient:
      'The sender will share a payment link when a payment is due. Paid another way? Record it and they’ll confirm.',
  },
} as const;
